import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  Grid,
  useMediaQuery,
  useTheme,
  Divider,
  Tooltip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import facturaVentaService, {
  FacturaVenta
} from '../../services/facturaVentaService';
import clienteService from '../../services/clienteService';
import { formatCurrency } from '../../utils/formatters';
import {
  Article as ArticleIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Summarize as SummarizeIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import PDFViewer from '../../components/shared/PDFViewer';
import SearchableSelect from '../../components/SearchableSelect';
import facturaCompraService, { LibroIvaComprasItem } from '../../services/facturaCompraService';
import companiaService, { Compania } from '../../services/companiaService';
import { buildInformeIvaSimple } from '../../utils/ivaSimple';
import { generarPdfIvaSimple } from '../../utils/pdfIvaSimple';
import {
  fetchCompaniaParaPdf,
  fetchLogoPngDataUrl,
  lineasDatosCompania,
  pdfLibroIvaTableWidth,
  pdfLibroIvaVentasColumnStyles,
  pdfLibroIvaVentasResumenColumnStyles,
  PDF_LIBRO_IVA_VENTAS_RESUMEN_HEAD,
  pdfFootLabelCell,
  pdfFootAmountCell,
  pdfLibroIvaFootStyles,
  PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS,
  type PdfFootCellDef
} from '../../utils/pdfLibroIvaCompania';
import { persistGlobalIdCompania, readGlobalIdCompania } from '../../utils/companiaPreferida';

// Función para formatear fecha en formato DD/MM/AAAA con ceros a la izquierda
const formatearFecha = (fecha: Date | string): string => {
  const date = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(date.getTime())) return '';
  
  const dia = date.getDate().toString().padStart(2, '0');
  const mes = (date.getMonth() + 1).toString().padStart(2, '0');
  const año = date.getFullYear();
  
  return `${dia}/${mes}/${año}`;
};

interface LibroIvaVentasRow {
  fechaEmision: string;
  tipoComprobante: string;
  puntoVenta: string; // Cambiado a string para mantener el formato con ceros
  numeroComprobante: string; // Cambiado a string para mantener el formato con ceros
  cliente: string;
  cuit: string;
  tipoIva: string;
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  total: number;
  nroFormulario: number;
}

interface LibroIvaVentasTotales {
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  total: number;
}

const LIBRO_IVA_VENTAS_TABLE_COL_COUNT = 13;

const totalesVentasInicial: LibroIvaVentasTotales = {
  netoGravado: 0,
  iva21: 0,
  iva105: 0,
  iva27: 0,
  exento: 0,
  total: 0
};

const sumLibroIvaVentasRows = (rows: LibroIvaVentasRow[]): LibroIvaVentasTotales =>
  rows.reduce(
    (acc, row) => ({
      netoGravado: acc.netoGravado + row.netoGravado,
      iva21: acc.iva21 + row.iva21,
      iva105: acc.iva105 + row.iva105,
      iva27: acc.iva27 + row.iva27,
      exento: acc.exento + row.exento,
      total: acc.total + row.total
    }),
    { ...totalesVentasInicial }
  );

const buildVentasFootRowPdf = (totales: LibroIvaVentasTotales): PdfFootCellDef[] => [
  pdfFootLabelCell('Totales', 7),
  pdfFootAmountCell(formatCurrency(totales.netoGravado), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva21), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva105), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva27), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.exento), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.total), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS)
];

const TIPO_IVA_ORDEN_PDF = ['Resp. Insc.', 'Monotributo', 'Exento', 'Cons. Final', 'No Categorizado'];

const agruparTotalesPorTipoIva = (rows: LibroIvaVentasRow[]): Map<string, LibroIvaVentasTotales> => {
  const grupos = new Map<string, LibroIvaVentasTotales>();

  rows.forEach((row) => {
    const key = row.tipoIva || '-';
    const prev = grupos.get(key) ?? { ...totalesVentasInicial };
    grupos.set(key, {
      netoGravado: prev.netoGravado + row.netoGravado,
      iva21: prev.iva21 + row.iva21,
      iva105: prev.iva105 + row.iva105,
      iva27: prev.iva27 + row.iva27,
      exento: prev.exento + row.exento,
      total: prev.total + row.total
    });
  });

  return grupos;
};

const ordenarTiposIvaPdf = (a: string, b: string): number => {
  const indexA = TIPO_IVA_ORDEN_PDF.indexOf(a);
  const indexB = TIPO_IVA_ORDEN_PDF.indexOf(b);
  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
  if (indexA !== -1) return -1;
  if (indexB !== -1) return 1;
  if (a === '-') return 1;
  if (b === '-') return -1;
  return a.localeCompare(b, 'es');
};

const buildVentasResumenPorTipoIvaBody = (rows: LibroIvaVentasRow[]): string[][] =>
  Array.from(agruparTotalesPorTipoIva(rows).entries())
    .sort(([tipoA], [tipoB]) => ordenarTiposIvaPdf(tipoA, tipoB))
    .map(([tipoIva, totales]) => [
      tipoIva,
      formatCurrency(totales.netoGravado),
      formatCurrency(totales.iva21),
      formatCurrency(totales.iva105),
      formatCurrency(totales.iva27),
      formatCurrency(totales.exento),
      formatCurrency(totales.total)
    ]);

const buildVentasResumenFootRowPdf = (totales: LibroIvaVentasTotales): PdfFootCellDef[] => [
  pdfFootLabelCell('Totales', 1),
  pdfFootAmountCell(formatCurrency(totales.netoGravado), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva21), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva105), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.iva27), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.exento), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
  pdfFootAmountCell(formatCurrency(totales.total), PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS)
];

const footerCellSx = { fontWeight: 700, bgcolor: '#f0f4f8', borderTop: '2px solid #bdbdbd' };

// Función para formatear el tipo de IVA
const formatearTipoIva = (tipoIva?: string): string => {
  if (!tipoIva) {
    return '-';
  }

  const normalized = tipoIva.toUpperCase();

  if (normalized === 'I' || normalized.includes('RESPONSABLE') || normalized.includes('INSCRIPTO')) {
    return 'Resp. Insc.';
  }

  if (normalized === 'M' || normalized.includes('MONOTRIBUTO')) {
    return 'Monotributo';
  }

  if (normalized === 'E' || normalized.includes('EXENTO')) {
    return 'Exento';
  }

  if (normalized === 'C' || normalized.includes('CONSUMIDOR') || normalized.includes('FINAL')) {
    return 'Cons. Final';
  }

  if (normalized === 'N' || normalized.includes('NO') || normalized.includes('CATEGORIZADO')) {
    return 'No Categorizado';
  }

  return tipoIva;
};

const LibroIvaVentas: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [facturas, setFacturas] = useState<FacturaVenta[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingTxt, setExportingTxt] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewPdfMeta, setPreviewPdfMeta] = useState({
    title: 'Libro IVA Ventas',
    documentId: 'libro-iva-ventas-preview',
    downloadFileName: `libro_iva_ventas_${new Date().toISOString().slice(0, 10)}.pdf`,
  });
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;
  const [totalesInforme, setTotalesInforme] = useState<LibroIvaVentasTotales>(totalesVentasInicial);
  const [loadingIvaSimple, setLoadingIvaSimple] = useState(false);
  const [clientesCache, setClientesCache] = useState<Map<number, string>>(new Map());
  const [companias, setCompanias] = useState<Compania[]>([]);

  // Inicializar fechas: primer día del mes actual y último día del mes actual
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [filters, setFilters] = useState({
    fechaDesde: firstDayOfMonth.toISOString().split('T')[0],
    fechaHasta: lastDayOfMonth.toISOString().split('T')[0],
    nroCliente: '',
    nroSuc: '',
    nombreCliente: '',
    idCompania: readGlobalIdCompania() ?? '',
  });

  const resolveCompaniaParaInforme = useCallback(async (): Promise<Compania | null> => {
    const id = filters.idCompania?.trim();
    if (id) {
      try {
        return await companiaService.getCompania(id);
      } catch {
        return null;
      }
    }
    return fetchCompaniaParaPdf();
  }, [filters.idCompania]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await companiaService.getCompanias({ pageNumber: 1, pageSize: 200 });
        if (!cancelled) {
          setCompanias(
            (list || []).map((c) => ({
              ...c,
              idCompania: String(c.idCompania || '').trim(),
            })),
          );
        }
      } catch {
        if (!cancelled) setCompanias([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await facturaVentaService.getLibroIvaVentas({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        fechaDesde: filters.fechaDesde || undefined,
        fechaHasta: filters.fechaHasta || undefined,
        nroCliente: filters.nroCliente ? parseInt(filters.nroCliente) : undefined,
        nroSuc: filters.nroSuc ? parseInt(filters.nroSuc) : undefined,
        nombreCliente: filters.nombreCliente || undefined,
        idCompania: filters.idCompania || undefined,
      });

      setFacturas(response.items);
      setTotalCount(response.totalCount);
      setTotalesInforme({
        netoGravado: response.totalImporteSinIva ?? 0,
        iva21: response.totalIvainscripto ?? 0,
        iva105: response.totalIvanoInscripto ?? 0,
        iva27: response.totalIvaotro ?? 0,
        exento: response.totalExento ?? 0,
        total: response.totalImporteTotal ?? 0
      });
      
      // Debug: verificar cuántos registros se recibieron
      console.log('Registros recibidos:', response.items.length, 'de', response.totalCount);

      // Cargar tipos de IVA de los clientes únicos
      const clientesUnicos = new Set<number>();
      response.items.forEach(factura => {
        if (factura.nroCliente) {
          clientesUnicos.add(factura.nroCliente);
        }
      });

      // Cargar los tipos de IVA de los clientes que no están en el cache
      const cacheActualizado = new Map(clientesCache);
      const nuevosClientes = Array.from(clientesUnicos).filter(nro => !clientesCache.has(nro));
      if (nuevosClientes.length > 0) {
        await Promise.all(
          nuevosClientes.map(async (nroCliente) => {
            try {
              const cliente = await clienteService.getCliente(nroCliente);
              if (cliente.tipoIva) {
                cacheActualizado.set(nroCliente, cliente.tipoIva);
              }
            } catch (error) {
              console.error(`Error al cargar cliente ${nroCliente}:`, error);
            }
          })
        );
        // Actualizar el cache del estado
        setClientesCache(cacheActualizado);
      }
    } catch (error) {
      console.error('Error al obtener el libro IVA ventas:', error);
      enqueueSnackbar('No se pudieron cargar los datos del libro IVA.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, page, rowsPerPage, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const handleClosePdfPreview = useCallback(() => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const name = event.target.name || '';
    const value = event.target.value;
    setFilters({ ...filters, [name]: value });
    setPage(0);
  };

  const obtenerTodasLasCompras = async (): Promise<LibroIvaComprasItem[]> => {
    const todasLasCompras: LibroIvaComprasItem[] = [];
    const pageSize = 20;
    let pageNumber = 1;
    let totalPages = 1;

    do {
      const response = await facturaCompraService.getLibroIvaCompras({
        pageNumber,
        pageSize,
        fechaDesde: filters.fechaDesde || undefined,
        fechaHasta: filters.fechaHasta || undefined,
        idCompania: filters.idCompania || undefined,
      });
      todasLasCompras.push(...response.items);
      totalPages = response.totalPages;
      pageNumber++;
    } while (pageNumber <= totalPages);

    return todasLasCompras;
  };

  const cargarClientesParaFilas = async (
    nrosCliente: number[],
    cacheBase: Map<number, string>
  ): Promise<Map<number, string>> => {
    const cache = new Map(cacheBase);
    const pendientes = nrosCliente.filter((nro) => !cache.has(nro));
    if (!pendientes.length) return cache;

    await Promise.all(
      pendientes.map(async (nroCliente) => {
        try {
          const cliente = await clienteService.getCliente(nroCliente);
          if (cliente.tipoIva) {
            cache.set(nroCliente, cliente.tipoIva);
          }
        } catch (error) {
          console.error(`Error al cargar cliente ${nroCliente}:`, error);
        }
      })
    );
    return cache;
  };

  // Convertir facturas a filas del libro IVA
  const convertToLibroIvaRows = (facturas: FacturaVenta[], cacheClientes: Map<number, string>): LibroIvaVentasRow[] => {
    return facturas.map(factura => {
      const tipoComprobante =
        (factura.tipoFormularioDescripcion ?? '').trim()
        || (factura.nroTipoFormulario ?? '').trim()
        || '-';

      const netoGravado = factura.importeSinIva ?? 0;
      const iva21 = factura.ivainscripto ?? 0;
      const iva105 = factura.ivanoInscripto ?? 0;
      const iva27 = factura.ivaotro ?? 0;
      const exento = factura.exento ?? 0;
      const total = factura.importeTotal ?? 0;

      const puntoVentaFormateado = (factura.nroSuc || 0).toString().padStart(5, '0');
      const numeroComprobanteFormateado = (factura.nroComprobante || 0).toString().padStart(8, '0');

      const fechaEmisionStr = factura.fechaEmision instanceof Date
        ? factura.fechaEmision.toISOString()
        : (factura.fechaEmision || '');

      return {
        fechaEmision: fechaEmisionStr,
        tipoComprobante,
        puntoVenta: puntoVentaFormateado,
        numeroComprobante: numeroComprobanteFormateado,
        cliente: factura.nombre || '-',
        cuit: factura.cuit || '-',
        tipoIva: formatearTipoIva(factura.nroCliente ? cacheClientes.get(factura.nroCliente) : undefined),
        netoGravado,
        iva21,
        iva105,
        iva27,
        exento,
        total,
        nroFormulario: factura.nroFormulario
      };
    });
  };

  // Función auxiliar para obtener todas las páginas de resultados
  const obtenerTodasLasPaginas = async (): Promise<FacturaVenta[]> => {
    const todasLasFacturas: FacturaVenta[] = [];
    const pageSize = 1000; // Tamaño de página razonable
    let pageNumber = 1;
    let totalPages = 1;

    do {
      const response = await facturaVentaService.getLibroIvaVentas({
        pageNumber,
        pageSize,
        fechaDesde: filters.fechaDesde || undefined,
        fechaHasta: filters.fechaHasta || undefined,
        nroCliente: filters.nroCliente ? parseInt(filters.nroCliente) : undefined,
        nroSuc: filters.nroSuc ? parseInt(filters.nroSuc) : undefined,
        nombreCliente: filters.nombreCliente || undefined,
        idCompania: filters.idCompania || undefined,
      });

      todasLasFacturas.push(...response.items);
      totalPages = response.totalPages;
      pageNumber++;
    } while (pageNumber <= totalPages);

    return todasLasFacturas;
  };

  const handleIvaSimple = async () => {
    try {
      setLoadingIvaSimple(true);

      const [todasLasFacturas, todasLasCompras, compania] = await Promise.all([
        obtenerTodasLasPaginas(),
        obtenerTodasLasCompras(),
        resolveCompaniaParaInforme(),
      ]);

      if (!todasLasFacturas.length && !todasLasCompras.length) {
        enqueueSnackbar('No hay datos para el informe IVA Simple en el período seleccionado.', { variant: 'warning' });
        return;
      }

      const clientesUnicos = new Set<number>();
      todasLasFacturas.forEach((factura) => {
        if (factura.nroCliente) clientesUnicos.add(factura.nroCliente);
      });
      const cacheIvaSimple = await cargarClientesParaFilas(Array.from(clientesUnicos), clientesCache);
      setClientesCache(cacheIvaSimple);

      const filasVentas = convertToLibroIvaRows(todasLasFacturas, cacheIvaSimple);
      const filasCompras = todasLasCompras.map((item) => {
        const tipoComprobante =
          (item.documento ?? '').trim() || (item.nroTipoFormulario ?? '').trim() || '-';
        return {
          tipoComprobante,
          tipoIva: formatearTipoIva(item.tipoIva),
          netoGravado: item.netoGrav ?? 0,
          iva21: item.iva21 ?? 0,
          iva105: item.iva10 ?? 0,
          iva27: item.iva27 ?? 0,
          exento: item.exento ?? 0,
          total: item.importeTotal ?? 0,
        };
      });

      const informe = buildInformeIvaSimple(filasVentas, filasCompras);
      const emitidoEn = new Date();
      const logoPng = compania?.idCompania
        ? await fetchLogoPngDataUrl(compania.idCompania)
        : null;

      const pdfBlob = await generarPdfIvaSimple({
        informe,
        compania,
        fechaDesde: filters.fechaDesde,
        fechaHasta: filters.fechaHasta,
        emitidoEn,
        logoPng,
      });

      const url = URL.createObjectURL(pdfBlob);
      setPreviewPdfMeta({
        title: 'IVA Simple',
        documentId: 'iva-simple-preview',
        downloadFileName: `iva_simple_${filters.fechaDesde}_${filters.fechaHasta}.pdf`,
      });
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('PDF IVA Simple generado.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar informe IVA Simple:', error);
      enqueueSnackbar('No se pudo generar el informe IVA Simple.', { variant: 'error' });
    } finally {
      setLoadingIvaSimple(false);
    }
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExporting(true);
      // Obtener todos los registros para la exportación (todas las páginas)
      const todasLasFacturas = await obtenerTodasLasPaginas();

      if (!todasLasFacturas.length) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      // Cargar tipos de IVA de los clientes únicos para la exportación
      const clientesUnicosExport = new Set<number>();
      todasLasFacturas.forEach(factura => {
        if (factura.nroCliente) {
          clientesUnicosExport.add(factura.nroCliente);
        }
      });

      const cacheExport = new Map(clientesCache);
      const nuevosClientesExport = Array.from(clientesUnicosExport).filter(nro => !clientesCache.has(nro));
      if (nuevosClientesExport.length > 0) {
        await Promise.all(
          nuevosClientesExport.map(async (nroCliente) => {
            try {
              const cliente = await clienteService.getCliente(nroCliente);
              if (cliente.tipoIva) {
                cacheExport.set(nroCliente, cliente.tipoIva);
              }
            } catch (error) {
              console.error(`Error al cargar cliente ${nroCliente}:`, error);
            }
          })
        );
      }

      const rows = convertToLibroIvaRows(todasLasFacturas, cacheExport);
      const totalesExport = sumLibroIvaVentasRows(rows);

      const data = rows.map((row) => ({
        'Fecha': row.fechaEmision ? formatearFecha(row.fechaEmision) : '',
        'Tipo': row.tipoComprobante,
        'Punto Venta': row.puntoVenta,
        'Número': row.numeroComprobante,
        'Cliente': row.cliente,
        'CUIT': row.cuit,
        'Tipo IVA': row.tipoIva,
        'Neto Gravado': row.netoGravado,
        'IVA 21%': row.iva21,
        'IVA 10.5%': row.iva105,
        'IVA 27%': row.iva27,
        'Exento': row.exento,
        'Total': row.total
      }));

      data.push({
        'Fecha': 'Totales',
        'Tipo': '',
        'Punto Venta': '',
        'Número': '',
        'Cliente': '',
        'CUIT': '',
        'Tipo IVA': '',
        'Neto Gravado': totalesExport.netoGravado,
        'IVA 21%': totalesExport.iva21,
        'IVA 10.5%': totalesExport.iva105,
        'IVA 27%': totalesExport.iva27,
        'Exento': totalesExport.exento,
        'Total': totalesExport.total
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Libro IVA Ventas');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `libro_iva_ventas_${today}.xlsx`);
      enqueueSnackbar('Exportación completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const parseInputDate = (value: string): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const downloadTextFile = (content: string, fileName: string) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportTxtArca = async () => {
    const fechaDesde = parseInputDate(filters.fechaDesde);
    const fechaHasta = parseInputDate(filters.fechaHasta);

    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Debe seleccionar fecha desde y fecha hasta para exportar TXT.', { variant: 'warning' });
      return;
    }

    if (fechaDesde > fechaHasta) {
      enqueueSnackbar('La fecha desde no puede ser mayor que la fecha hasta.', { variant: 'warning' });
      return;
    }

    try {
      setExportingTxt(true);
      const result = await facturaVentaService.exportLibroIvaVentasArcaTxt({
        fechaDesde,
        fechaHasta,
        idCompania: filters.idCompania || undefined,
      });

      downloadTextFile(result.comprobantesContent, result.comprobantesFileName);
      downloadTextFile(result.alicuotasContent, result.alicuotasFileName);
      enqueueSnackbar('Archivos TXT generados para ARCA.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar TXT ARCA:', error);
      enqueueSnackbar('No se pudieron generar los archivos TXT para ARCA.', { variant: 'error' });
    } finally {
      setExportingTxt(false);
    }
  };

  const handleExportPdf = async () => {
    if (totalCount === 0) {
      enqueueSnackbar('No hay información para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPdf(true);
      // Obtener todos los registros para la exportación PDF (todas las páginas)
      const todasLasFacturas = await obtenerTodasLasPaginas();

      if (!todasLasFacturas.length) {
        enqueueSnackbar('No hay información para exportar.', { variant: 'warning' });
        return;
      }

      // Cargar tipos de IVA de los clientes únicos para la exportación PDF
      const clientesUnicosPdf = new Set<number>();
      todasLasFacturas.forEach(factura => {
        if (factura.nroCliente) {
          clientesUnicosPdf.add(factura.nroCliente);
        }
      });

      const cachePdf = new Map(clientesCache);
      const nuevosClientesPdf = Array.from(clientesUnicosPdf).filter(nro => !clientesCache.has(nro));
      if (nuevosClientesPdf.length > 0) {
        await Promise.all(
          nuevosClientesPdf.map(async (nroCliente) => {
            try {
              const cliente = await clienteService.getCliente(nroCliente);
              if (cliente.tipoIva) {
                cachePdf.set(nroCliente, cliente.tipoIva);
              }
            } catch (error) {
              console.error(`Error al cargar cliente ${nroCliente}:`, error);
            }
          })
        );
      }

      const rows = convertToLibroIvaRows(todasLasFacturas, cachePdf);
      const totalesExport = sumLibroIvaVentasRows(rows);

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const pageW = doc.internal.pageSize.getWidth();
      const headerTop = 32;
      const logoMaxW = 76;
      const logoMaxH = 42;
      const rightColW = 260;
      const rightX = pageW - marginX;

      const companiaSeleccionada = Boolean(filters.idCompania?.trim());
      const compania = await resolveCompaniaParaInforme();
      const logoPng =
        companiaSeleccionada && compania?.idCompania
          ? await fetchLogoPngDataUrl(compania.idCompania)
          : null;

      if (logoPng) {
        doc.addImage(logoPng, 'PNG', marginX, headerTop, logoMaxW, logoMaxH, undefined, 'FAST');
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.setTextColor(25, 55, 95);
      doc.text('Libro IVA Ventas', pageW / 2, headerTop + 26, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);

      doc.setFontSize(7);
      const datosComp =
        companiaSeleccionada && compania ? lineasDatosCompania(compania) : [];
      let ry = headerTop + 4;
      datosComp.forEach((linea) => {
        const wrapped = doc.splitTextToSize(linea, rightColW);
        wrapped.forEach((sub: string) => {
          doc.text(sub, rightX, ry, { align: 'right' });
          ry += 8;
        });
      });

      const headerBottom = Math.max(
        logoPng ? headerTop + logoMaxH + 4 : headerTop,
        ry + 4,
        headerTop + 48,
      );
      let currentY = headerBottom;

      doc.setFontSize(8);
      const generadoEn = new Date();
      doc.text(`Generado: ${generadoEn.toLocaleString('es-AR')}`, marginX, currentY);
      currentY += 12;

      if (filters.fechaDesde && filters.fechaHasta) {
        const fechaDesdeParts = filters.fechaDesde.split('-');
        const fechaHastaParts = filters.fechaHasta.split('-');
        const fechaDesdeLocal = new Date(
          parseInt(fechaDesdeParts[0], 10),
          parseInt(fechaDesdeParts[1], 10) - 1,
          parseInt(fechaDesdeParts[2], 10)
        );
        const fechaHastaLocal = new Date(
          parseInt(fechaHastaParts[0], 10),
          parseInt(fechaHastaParts[1], 10) - 1,
          parseInt(fechaHastaParts[2], 10)
        );
        doc.text(
          `Período: ${formatearFecha(fechaDesdeLocal)} - ${formatearFecha(fechaHastaLocal)}`,
          marginX,
          currentY
        );
        currentY += 14;
      }

      const autoTable = (await import('jspdf-autotable')).default;
      const tableWidth = pdfLibroIvaTableWidth(pageW, marginX);

      autoTable(doc, {
        startY: currentY,
        tableWidth,
        head: [['Fecha', 'Tipo', 'P.Vta', 'Número', 'Cliente', 'CUIT', 'Tipo IVA', 'Neto Grav.', 'IVA 21%', 'IVA 10.5%', 'IVA 27%', 'Exento', 'Total']],
        body: rows.map((row) => [
          row.fechaEmision ? formatearFecha(row.fechaEmision) : '-',
          row.tipoComprobante,
          row.puntoVenta,
          row.numeroComprobante,
          row.cliente,
          row.cuit,
          row.tipoIva,
          formatCurrency(row.netoGravado),
          formatCurrency(row.iva21),
          formatCurrency(row.iva105),
          formatCurrency(row.iva27),
          formatCurrency(row.exento),
          formatCurrency(row.total)
        ]),
        foot: [buildVentasFootRowPdf(totalesExport)],
        showFoot: 'lastPage',
        margin: { left: marginX, right: marginX },
        styles: {
          fontSize: 5.5,
          cellPadding: 1,
          overflow: 'ellipsize',
          valign: 'middle',
          minCellHeight: 9
        },
        headStyles: { fillColor: [245, 158, 11], textColor: 255, fontSize: 6, cellPadding: 1.2 },
        bodyStyles: { fontSize: 5.5 },
        footStyles: pdfLibroIvaFootStyles(PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
        columnStyles: pdfLibroIvaVentasColumnStyles(tableWidth)
      });

      const resumenPorTipoIva = buildVentasResumenPorTipoIvaBody(rows);
      if (resumenPorTipoIva.length > 0) {
        const docConTabla = doc as typeof doc & { lastAutoTable?: { finalY: number } };
        const pageH = doc.internal.pageSize.getHeight();
        let resumenY = (docConTabla.lastAutoTable?.finalY ?? currentY) + 22;

        if (resumenY > pageH - 72) {
          doc.addPage();
          resumenY = 48;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(25, 55, 95);
        doc.text('Resumen por Tipo IVA', marginX, resumenY);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        resumenY += 12;

        autoTable(doc, {
          startY: resumenY,
          tableWidth,
          head: [Array.from(PDF_LIBRO_IVA_VENTAS_RESUMEN_HEAD)],
          body: resumenPorTipoIva,
          foot: [buildVentasResumenFootRowPdf(totalesExport)],
          showFoot: 'lastPage',
          margin: { left: marginX, right: marginX },
          styles: {
            fontSize: 5.5,
            cellPadding: 1,
            overflow: 'linebreak',
            valign: 'middle',
            minCellHeight: 9
          },
          headStyles: { fillColor: [245, 158, 11], textColor: 255, fontSize: 6, cellPadding: 1.2 },
          bodyStyles: { fontSize: 5.5 },
          footStyles: pdfLibroIvaFootStyles(PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS),
          columnStyles: pdfLibroIvaVentasResumenColumnStyles(tableWidth)
        });
      }

      const pdfBlob = doc.output('blob');
      const safeBlob =
        pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
          ? pdfBlob
          : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(safeBlob);
      setPreviewPdfMeta({
        title: 'Libro IVA Ventas',
        documentId: 'libro-iva-ventas-preview',
        downloadFileName: `libro_iva_ventas_${new Date().toISOString().slice(0, 10)}.pdf`,
      });
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        enqueueSnackbar('Exportación cancelada por el usuario.', { variant: 'warning' });
      } else {
        console.error('Error al generar el PDF:', error);
        enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const renderFooter = () => {
    if (loading || facturas.length === 0) {
      return null;
    }

    return (
      <TableRow>
        <TableCell colSpan={7} sx={footerCellSx}>
          Totales
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesInforme.netoGravado)}
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesInforme.iva21)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesInforme.iva105)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesInforme.iva27)}
        </TableCell>
        <TableCell align="right" sx={{ ...footerCellSx, display: { xs: 'none', lg: 'table-cell' } }}>
          {formatCurrency(totalesInforme.exento)}
        </TableCell>
        <TableCell align="right" sx={footerCellSx}>
          {formatCurrency(totalesInforme.total)}
        </TableCell>
      </TableRow>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={LIBRO_IVA_VENTAS_TABLE_COL_COUNT} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (!loading && facturas.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={LIBRO_IVA_VENTAS_TABLE_COL_COUNT} align="center">
            No hay facturas para el período seleccionado.
          </TableCell>
        </TableRow>
      );
    }

    const rows = convertToLibroIvaRows(facturas, clientesCache);
    
    // Debug: verificar cuántos registros se están mostrando
    console.log('Registros a mostrar:', rows.length, 'de facturas:', facturas.length, 'totalCount:', totalCount);

    return (
      <>
        {rows.map((row, index) => {
          // Usar index como parte de la key para evitar problemas con duplicados
          return (
            <TableRow key={`${row.nroFormulario}-${index}`} hover>
              <TableCell>
                {row.fechaEmision ? formatearFecha(row.fechaEmision) : '-'}
              </TableCell>
              <TableCell>{row.tipoComprobante}</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {row.puntoVenta}
              </TableCell>
              <TableCell align="center">{row.numeroComprobante}</TableCell>
              <TableCell>{row.cliente}</TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{row.cuit}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.tipoIva}</TableCell>
              <TableCell align="right">{formatCurrency(row.netoGravado)}</TableCell>
              <TableCell align="right">{formatCurrency(row.iva21)}</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                {formatCurrency(row.iva105)}
              </TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                {formatCurrency(row.iva27)}
              </TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                {formatCurrency(row.exento)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatCurrency(row.total)}
              </TableCell>
            </TableRow>
          );
        })}
      </>
    );
  };

  const renderCards = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={40} />
        </Box>
      );
    }

    if (!loading && facturas.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography variant="body1" color="text.secondary">
            No hay facturas para el período seleccionado.
          </Typography>
        </Box>
      );
    }

    const rows = convertToLibroIvaRows(facturas, clientesCache);

    return (
      <Stack spacing={2}>
        {rows.map((row, index) => (
          <Card
            key={`${row.nroFormulario}-${index}`}
            sx={{
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent>
              <Stack spacing={1.5}>
                {/* Header con fecha y tipo */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {row.tipoComprobante}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row.fechaEmision ? formatearFecha(row.fechaEmision) : '-'}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {formatCurrency(row.total)}
                  </Typography>
                </Stack>

                <Divider />

                {/* Información del comprobante */}
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      P.Vta / Número:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.puntoVenta} - {row.numeroComprobante}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Cliente:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>
                      {row.cliente}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      CUIT:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.cuit}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Tipo IVA:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.tipoIva}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider />

                {/* Importes */}
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Neto Gravado:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(row.netoGravado)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      IVA 21%:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(row.iva21)}
                    </Typography>
                  </Stack>
                  {row.iva105 !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        IVA 10.5%:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.iva105)}
                      </Typography>
                    </Stack>
                  )}
                  {row.iva27 !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        IVA 27%:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.iva27)}
                      </Typography>
                    </Stack>
                  )}
                  {row.exento !== 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Exento:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.exento)}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  };

  const renderMobileTotales = () => {
    if (loading || facturas.length === 0) {
      return null;
    }

    return (
      <Card sx={{ mt: 2, borderRadius: 2, border: '2px solid #bdbdbd', bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Totales del informe
          </Typography>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Neto Gravado</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.netoGravado)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 21%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.iva21)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 10.5%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.iva105)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">IVA 27%</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.iva27)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Exento</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(totalesInforme.exento)}</Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body1" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#1976d2' }}>{formatCurrency(totalesInforme.total)}</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título y botones */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            sx={{ mb: 3 }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/ventas/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: '#1976d2'
                }}
              >
                Libro IVA Ventas
              </Typography>
            </Stack>
            <Stack 
              direction="row" 
              spacing={1}
              flexWrap="wrap"
              useFlexGap
            >
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SummarizeIcon />}
                onClick={() => void handleIvaSimple()}
                disabled={loading || loadingIvaSimple}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {loadingIvaSimple ? 'Generando PDF…' : 'IVA Simple'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={loading || exporting}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                Exportar Excel
              </Button>
              <Tooltip title="Genera los archivos TXT de comprobantes y alícuotas para la presentación del Libro IVA Digital Ventas ante ARCA.">
                <Box component="span" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArticleIcon />}
                    onClick={() => void handleExportTxtArca()}
                    disabled={loading || exportingTxt}
                    sx={{
                      borderRadius: 2,
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      width: { xs: '100%', sm: 'auto' },
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    {exportingTxt ? 'Generando TXT…' : 'Exportar TXT'}
                  </Button>
                </Box>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => void handleExportPdf()}
                disabled={loading || exportingPdf}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {exportingPdf ? 'Generando PDF…' : 'Imprimir PDF'}
              </Button>
            </Stack>
          </Stack>

          {/* Filtros */}
          <Card sx={{ mb: 3, bgcolor: '#fafafa' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Compañía"
                    allowEmpty
                    emptyOptionLabel="Todas las compañías"
                    value={filters.idCompania || null}
                    onChange={(v) => {
                      const id = v ?? '';
                      if (id) persistGlobalIdCompania(id);
                      setFilters((prev) => ({ ...prev, idCompania: id }));
                      setPage(0);
                    }}
                    options={companias.map((c) => ({
                      value: c.idCompania,
                      label: `${c.idCompania} - ${c.nombre}`,
                    }))}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha Desde"
                    type="date"
                    name="fechaDesde"
                    value={filters.fechaDesde}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha Hasta"
                    type="date"
                    name="fechaHasta"
                    value={filters.fechaHasta}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nro. Cliente"
                    name="nroCliente"
                    value={filters.nroCliente}
                    onChange={handleFilterChange}
                    size="small"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nro. Sucursal"
                    name="nroSuc"
                    value={filters.nroSuc}
                    onChange={handleFilterChange}
                    size="small"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nombre Cliente"
                    name="nombreCliente"
                    value={filters.nombreCliente}
                    onChange={handleFilterChange}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {isMobile ? (
            <Box>
              {renderCards()}
              {renderMobileTotales()}
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  mt: 2,
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Box>
          ) : (
            /* Vista desktop: Tabla */
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                overflowX: 'auto',
                maxWidth: '100%',
                '& .MuiTableCell-root': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }
              }}
            >
              <Table size="small" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 80, sm: 100 } }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 } }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'center', minWidth: { xs: 60, sm: 80 }, display: { xs: 'none', md: 'table-cell' } }}>P.Vta</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'center', minWidth: { xs: 80, sm: 100 } }}>Número</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 150, sm: 200 } }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 }, display: { xs: 'none', lg: 'table-cell' } }}>CUIT</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 100, sm: 120 }, display: { xs: 'none', md: 'table-cell' } }}>Tipo IVA</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 100, sm: 120 } }}>Neto Gravado</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 } }}>IVA 21%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>IVA 10.5%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>IVA 27%</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 80, sm: 100 }, display: { xs: 'none', lg: 'table-cell' } }}>Exento</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right', minWidth: { xs: 100, sm: 120 } }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderBody()}
                  {renderFooter()}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: { xs: 1, sm: 2 }
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title={previewPdfMeta.title}
          documentId={previewPdfMeta.documentId}
          documentType="informeCliente"
          downloadFileName={previewPdfMeta.downloadFileName}
        />
      ) : null}
    </Box>
  );
};

export default LibroIvaVentas;

